/**
 * Boli Live - Inbox/Notifications Page
 *
 * Users can view their notifications here with real-time updates.
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Bell, Gavel, Trophy, Package, AlertCircle, CheckCheck, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNotificationStore } from '../lib/stores/notification-store';
import type { Notification, NotificationType } from '@boli/types';

// Get icon for notification type
function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'outbid':
      return <Gavel className="w-5 h-5 text-orange-500" />;
    case 'auction_won':
      return <Trophy className="w-5 h-5 text-yellow-500" />;
    case 'auction_ended':
      return <Bell className="w-5 h-5 text-blue-500" />;
    case 'order_update':
      return <Package className="w-5 h-5 text-green-500" />;
    case 'system':
    default:
      return <AlertCircle className="w-5 h-5 text-gray-500" />;
  }
}

// Get link for notification based on related type
function getNotificationLink(notification: Notification): string | null {
  if (!notification.relatedId || !notification.relatedType) return null;

  switch (notification.relatedType) {
    case 'auction':
      return `/auction/${notification.relatedId}`;
    case 'order':
      return `/orders`;
    case 'product':
      return `/product/${notification.relatedId}`;
    default:
      return null;
  }
}

// Format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-PK', { month: 'short', day: 'numeric' });
}

// Notification card component
function NotificationCard({
  notification,
  onMarkAsRead,
}: {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
}) {
  const link = getNotificationLink(notification);

  const content = (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border transition-all ${
        notification.isRead
          ? 'bg-background border-border'
          : 'bg-blue-50 border-blue-100 hover:bg-blue-100/50'
      }`}
      onClick={() => {
        if (!notification.isRead) {
          onMarkAsRead(notification.id);
        }
      }}
    >
      {/* Icon */}
      <div className="flex-shrink-0 mt-0.5">
        {getNotificationIcon(notification.type)}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className={`text-sm font-medium ${notification.isRead ? 'text-foreground' : 'text-foreground'}`}>
            {notification.title}
          </h3>
          {!notification.isRead && (
            <span className="flex-shrink-0 w-2 h-2 mt-1.5 bg-blue-500 rounded-full" />
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
          {notification.content}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {formatRelativeTime(notification.createdAt)}
        </p>
      </div>
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="block">
        {content}
      </Link>
    );
  }

  return <div className="cursor-pointer">{content}</div>;
}

export default function InboxPage() {
  const {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotificationStore();

  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');

  // Fetch notifications on mount and tab change
  useEffect(() => {
    fetchNotifications({
      limit: 50,
      unreadOnly: activeTab === 'unread',
    });
  }, [fetchNotifications, activeTab]);

  const filteredNotifications = activeTab === 'unread'
    ? notifications.filter((n) => !n.isRead)
    : notifications;

  return (
    <div className="min-h-screen bg-background px-4 pt-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl mx-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-foreground">Notifications</h1>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors"
            >
              <CheckCheck className="w-4 h-4" />
              Mark all as read
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('all')}
            className={`pb-3 px-2 font-medium transition-colors ${
              activeTab === 'all'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab('unread')}
            className={`pb-3 px-2 font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'unread'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Unread
            {unreadCount > 0 && (
              <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* Notification List */}
        {!isLoading && filteredNotifications.length > 0 && (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                onMarkAsRead={markAsRead}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredNotifications.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mb-6">
              <Bell className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {activeTab === 'unread' ? 'All caught up!' : 'No notifications yet'}
            </h2>
            <p className="text-muted-foreground max-w-xs">
              {activeTab === 'unread'
                ? "You've read all your notifications. Check back later for updates."
                : "When you receive notifications about auctions, orders, or updates, they'll appear here."}
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
